import styled from '@emotion/styled';
import { Button, Chip, theme } from '@travelnest/hui';
import * as React from 'react';
import { connect } from 'react-redux';
import { currentUserId } from '../../../entities/auth/session/selectors';
import { IThunkDispatch } from '../../../store/middleware';
import { IState } from '../../../store/reducer';
import { useDataLoad } from '../../../view/helpers/hooks';
import useLocalStorage from '../../../view/helpers/useLocalStorage';
import { getMarketingPreferences } from '../../../view/pages/marketing/actions';
import { IMarketingPreferences } from '../../../view/pages/marketing/home/types';
import StyledHeading from '../typography/StyledHeading';
import StyledText from '../typography/StyledText';

const Card = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing.space30};
	padding: ${theme.spacing.space50};
	border-radius: ${theme.radii.radii40};
	border: 1px solid rgba(5, 42, 128, 0.1);
	background: rgba(247, 255, 252, 1);
    overflow: hidden;
    position: relative;
    a {
        color: rgba(67, 104, 231, 1);
        text-decoration: none;
    }
    p {
        margin: 0;
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.space30};
`;

const Title = styled(StyledHeading)`
	color: ${theme.colors.LM_NU100};
	margin: 0;
`;

const ButtonRow = styled.div`
    display: flex;
    gap: ${theme.spacing.space30};
`;

const ImgContainer = styled.div`
    width: 255px;
    height: auto;
    flex-shrink: 0;
    position: relative;

    @media only screen and (max-width: 768px) {
        display: none;
    }
`;

const StyledImg = styled.img`
    position: absolute;
    width: 100%;
`;

const mapDispatchToProps = (dispatch: IThunkDispatch) => ({
	getMarketingPreferences: (customerId: string) => dispatch(getMarketingPreferences(customerId)),
});

const mapStateToProps = () => (state: IState) => {
	const customerId = currentUserId(state);
	return {
		marketingPreferences: state.views.marketing.home.drafts[customerId] || {},
	};
};

interface INewCancellationPolicyCardContainerProps {
	marketingPreferences: IMarketingPreferences;
	customerId?: string;
	getMarketingPreferences(customerId: string): Promise<any>;
}

const NewCancellationPolicyCardContainer: React.FC<INewCancellationPolicyCardContainerProps> = (props) => {
	const [hasDismissed, setHasDismissed] = useLocalStorage<boolean>('tn-has-dismissed-new-cancellation-policy-notice', false);
	const { cancellationPolicy } = props.marketingPreferences;
	const loading = useDataLoad(!cancellationPolicy, () => props.getMarketingPreferences(props?.customerId || ''));

	const isOnNewCancellationPolicy = cancellationPolicy?.includes('non_refundable_v1');

	return (!loading && !hasDismissed && isOnNewCancellationPolicy) ? (
		<Card>
			<Column>
				<Title
					htmlElement="h3"
					lineHeight="sm"
					type="IB110"
				>
					<Chip text="New" color="green" />
					{` Your non-refundable rate is here`}
				</Title>
				<StyledText
					type="IR100"
					lineHeight="md"
				>
					{`Our recommended flexible cancellation preference has been expanded to include a non-refundable rate option. Helping you enhance your revenue and minimise cancellations. `}
					<a href="https://travelnest.freshdesk.com/support/solutions/articles/101000488627-which-cancellation-preference-is-right-for-me-"
						target="_blank">Read more.</a>
				</StyledText>
				<ButtonRow>
					<Button
						text="Go to cancellation preferences"
						variant="positive"
						fill="light"
						onClick={() => {
							window.location.assign(`${window.location.origin}/channels`);
						}}
					/>
					<Button
						text="Dismiss"
						variant="positive"
						fill="clear"
						role="link"
						onClick={() => setHasDismissed(true)}
					/>
				</ButtonRow>
			</Column>
			<ImgContainer>
				<StyledImg src="/71eb904/assets/images/cancellation-pref.png" />
			</ImgContainer>
		</Card >
	) : null;
};

export const NewCancellationPolicyCard = connect(mapStateToProps, mapDispatchToProps)(NewCancellationPolicyCardContainer);
